'use client';

import { Button, ButtonType } from '@/components/ui/button';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';

import type { SiteNavNodeView } from '@/components/site-page/SitePage.types';
import { SiteHeaderDropdown } from './SiteHeaderDropdown';
import { SiteHeaderNavLink, SiteHeaderNodeContent } from './SiteHeaderParts';
import { SiteHeaderTooltip } from './SiteHeaderTooltip';
import {
  groupWrapperClasses,
  iconClasses,
  navItemClasses,
  tooltipWrapperClasses,
} from './SiteHeader.style';
import { HOVER_OPEN_DELAY_MS, type OpenBy } from './SiteHeader.types';
import { currentMark, useNavLocation } from './site-header-utils';

interface SiteHeaderNodeProps {
  node: SiteNavNodeView;
  /** Render icon-only (a label-less item, or a CENTER item shed at 768-1023 px). */
  iconOnly: boolean;
  open: boolean;
  openBy: OpenBy | null;
  onOpenRequest: (id: string, by: OpenBy) => void;
  onCloseRequest: (id: string) => void;
}

/**
 * A top-level item of a desktop zone: a link, a pop-up button, or a group toggle with its dropdown.
 *
 * ## Invariants:
 * - Hover opens a group after `HOVER_OPEN_DELAY_MS` and closes a hover-opened panel on leave, but
 *   never closes a click- or key-opened one.
 * - Escape closes the panel and returns focus to the toggle. The panel also closes on an outside
 *   click and when focus tabs to an element outside the group.
 * - An icon-only item announces `aria-label` and shows an inline tooltip on hover and focus.
 * - `site-header-node` is ONLY a top-level PAGE / LINK control (with `data-node-id`,
 *   `data-node-kind`). A GROUP's wrapper carries no `data-cy`; its toggle is
 *   `site-header-group-toggle` (`data-node-kind="GROUP"`), so a zone's `site-header-node` count
 *   never includes groups (Surface Spec Test Selectors).
 */
export function SiteHeaderNode({
  node,
  iconOnly,
  open,
  openBy,
  onOpenRequest,
  onCloseRequest,
}: SiteHeaderNodeProps) {
  const location = useNavLocation();
  const mark = currentMark(node, location);
  const tooltipId = useId();
  const panelId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tipVisible, setTipVisible] = useState(false);

  const clearHover = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }, []);
  useEffect(() => clearHover, [clearHover]);

  const close = useCallback(
    (returnFocus: boolean) => {
      onCloseRequest(node.id);
      if (returnFocus) toggleRef.current?.focus();
    },
    [node.id, onCloseRequest]
  );

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) onCloseRequest(node.id);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, node.id, onCloseRequest]);

  const name = iconOnly ? (node.label ?? node.a11yLabel) : node.a11yLabel;
  const needsAriaLabel = iconOnly || !node.label || node.a11yLabel !== node.label;
  const ariaLabel = needsAriaLabel ? name : undefined;
  const showTooltip = iconOnly && tipVisible;
  const className = navItemClasses({ current: mark !== false });
  const ariaCurrent = mark === 'page' ? ('page' as const) : undefined;

  const tooltipHandlers = iconOnly
    ? {
        onMouseEnter: () => setTipVisible(true),
        onMouseLeave: () => setTipVisible(false),
        onFocus: () => setTipVisible(true),
        onBlur: () => setTipVisible(false),
      }
    : {};
  const describedBy = showTooltip ? tooltipId : undefined;
  const content = (
    <SiteHeaderNodeContent
      node={node}
      showLabel={!iconOnly}
      iconCy="site-header-node-icon"
      labelCy="site-header-node-label"
    />
  );
  const tooltip = showTooltip ? <SiteHeaderTooltip id={tooltipId} text={name} /> : null;

  if (node.kind !== 'GROUP') {
    return (
      <span className={tooltipWrapperClasses} {...tooltipHandlers}>
        {node.link ? (
          <SiteHeaderNavLink
            link={node.link}
            className={className}
            data-cy="site-header-node"
            data-node-id={node.id}
            data-node-kind={node.kind}
            aria-label={ariaLabel}
            aria-current={ariaCurrent}
            aria-describedby={describedBy}
          >
            {content}
          </SiteHeaderNavLink>
        ) : (
          <Button
            buttonType={ButtonType.text}
            className={className}
            data-cy="site-header-node"
            data-node-id={node.id}
            data-node-kind={node.kind}
            aria-label={ariaLabel}
            aria-describedby={describedBy}
            onClick={node.onActivate}
          >
            {content}
          </Button>
        )}
        {tooltip}
      </span>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={groupWrapperClasses}
      onMouseEnter={() => {
        setTipVisible(iconOnly);
        if (open) return;
        clearHover();
        hoverTimer.current = setTimeout(() => onOpenRequest(node.id, 'hover'), HOVER_OPEN_DELAY_MS);
      }}
      onMouseLeave={() => {
        setTipVisible(false);
        clearHover();
        if (open && openBy === 'hover') close(false);
      }}
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        if (next && !wrapperRef.current?.contains(next)) close(false);
      }}
    >
      <Button
        buttonType={ButtonType.text}
        ref={toggleRef}
        className={className}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? panelId : undefined}
        aria-label={ariaLabel}
        aria-current={mark === 'ancestor' ? 'true' : undefined}
        aria-describedby={describedBy}
        data-cy="site-header-group-toggle"
        data-node-id={node.id}
        data-node-kind={node.kind}
        onClick={(event) => {
          clearHover();
          if (!open) onOpenRequest(node.id, event.detail === 0 ? 'key' : 'click');
          else if (openBy === 'hover') onOpenRequest(node.id, 'click');
          else close(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            onOpenRequest(node.id, 'key');
          } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            close(true);
          }
        }}
        onFocus={() => setTipVisible(iconOnly)}
      >
        {content}
        <MdKeyboardArrowDown size={20} className={iconClasses} aria-hidden="true" />
      </Button>
      {tooltip}
      {open && (
        <SiteHeaderDropdown
          node={node}
          panelId={panelId}
          focusFirst={openBy === 'key'}
          onClose={close}
        />
      )}
    </div>
  );
}
